import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import * as mcpApi from '@/lib/mcp/api';
import type { UnifiedTool, ToolSource } from '@/lib/mcp/types';
import {
  ArrowLeft,
  Play,
  Copy,
  Star,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_dashboard/tools/$id')({
  component: ToolDetailPage,
});

const SOURCE_LABELS: Record<ToolSource, string> = {
  native_utility: 'Native Utility',
  provider_endpoint: 'Provider Endpoint',
  external_mcp: 'External MCP Server',
};

interface SchemaProperty {
  type?: string;
  description?: string;
  default?: unknown;
  enum?: string[];
  items?: SchemaProperty;
  properties?: Record<string, SchemaProperty>;
  required?: string[];
  additionalProperties?: boolean | SchemaProperty;
}

function ToolDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toolId = decodeURIComponent(id);

  const [tool, setTool] = useState<UnifiedTool | null>(null);
  const [loading, setLoading] = useState(true);
  const [invoking, setInvoking] = useState(false);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [result, setResult] = useState<{
    success: boolean;
    data?: unknown;
    error?: string;
    durationMs: number;
  } | null>(null);

  const fetchTool = useCallback(async () => {
    setLoading(true);
    try {
      const response = await mcpApi.getTool(toolId);
      setTool(response.data);

      // Initialize form data with defaults
      const schema = response.data.inputSchema as { properties?: Record<string, SchemaProperty> };
      const defaults: Record<string, unknown> = {};
      if (schema.properties) {
        for (const [key, prop] of Object.entries(schema.properties)) {
          if (prop.default !== undefined) {
            defaults[key] = prop.default;
          }
        }
      }
      setFormData(defaults);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to fetch tool');
      navigate({ to: '/tools' });
    } finally {
      setLoading(false);
    }
  }, [toolId, navigate]);

  useEffect(() => {
    fetchTool();
  }, [fetchTool]);

  const handleInvoke = async () => {
    if (!tool) return;

    setInvoking(true);
    setResult(null);

    try {
      const response = await mcpApi.invokeTool(tool.id, formData);
      setResult({
        success: response.success,
        data: response.data,
        error: response.error,
        durationMs: response.durationMs,
      });

      if (response.success) {
        toast.success('Tool executed successfully');
      } else {
        toast.error(response.error || 'Tool execution failed');
      }
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to invoke tool';
      setResult({
        success: false,
        error: errorMessage,
        durationMs: 0,
      });
      toast.error(errorMessage);
    } finally {
      setInvoking(false);
    }
  };

  const handleInputChange = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleCopyResult = () => {
    if (result?.data) {
      navigator.clipboard.writeText(
        typeof result.data === 'string' ? result.data : JSON.stringify(result.data, null, 2)
      );
      toast.success('Copied to clipboard');
    }
  };

  const renderInputField = (key: string, property: SchemaProperty, required: boolean) => {
    const value = formData[key];

    if (property.type === 'boolean') {
      return (
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id={key}
            checked={Boolean(value)}
            onChange={(e) => handleInputChange(key, e.target.checked)}
            className="h-4 w-4"
          />
          <Label htmlFor={key} className="text-sm font-normal">
            {property.description || key}
          </Label>
        </div>
      );
    }

    if (property.enum) {
      return (
        <div className="space-y-2">
          <Label htmlFor={key}>
            {key}
            {required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <select
            id={key}
            value={String(value || '')}
            onChange={(e) => handleInputChange(key, e.target.value)}
            className="w-full p-2 border rounded-md bg-background"
          >
            <option value="">Select...</option>
            {property.enum.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {property.description && (
            <p className="text-xs text-muted-foreground">{property.description}</p>
          )}
        </div>
      );
    }

    if (property.type === 'number' || property.type === 'integer') {
      return (
        <div className="space-y-2">
          <Label htmlFor={key}>
            {key}
            {required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Input
            id={key}
            type="number"
            value={String(value || '')}
            onChange={(e) =>
              handleInputChange(key, e.target.value ? Number(e.target.value) : undefined)
            }
            placeholder={property.description || `Enter ${key}`}
          />
          {property.description && (
            <p className="text-xs text-muted-foreground">{property.description}</p>
          )}
        </div>
      );
    }

    if (property.type === 'object') {
      return (
        <div className="space-y-2">
          <Label htmlFor={key}>
            {key}
            {required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Textarea
            id={key}
            value={typeof value === 'string' ? value : JSON.stringify(value || {}, null, 2)}
            onChange={(e) => {
              try {
                handleInputChange(key, JSON.parse(e.target.value));
              } catch {
                handleInputChange(key, e.target.value);
              }
            }}
            placeholder={property.description || 'Enter JSON object'}
            rows={4}
            className="font-mono text-sm"
          />
          {property.description && (
            <p className="text-xs text-muted-foreground">{property.description}</p>
          )}
        </div>
      );
    }

    // Default: string input
    return (
      <div className="space-y-2">
        <Label htmlFor={key}>
          {key}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <Input
          id={key}
          value={String(value || '')}
          onChange={(e) => handleInputChange(key, e.target.value || undefined)}
          placeholder={property.description || `Enter ${key}`}
        />
        {property.description && (
          <p className="text-xs text-muted-foreground">{property.description}</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div>
            <Skeleton className="h-6 w-48 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!tool) {
    return null;
  }

  const schema = tool.inputSchema as {
    properties?: Record<string, SchemaProperty>;
    required?: string[];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: '/tools' })}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{tool.name}</h1>
          <p className="text-muted-foreground mt-1">{tool.description}</p>
        </div>
      </div>

      {/* Metadata */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="secondary">{SOURCE_LABELS[tool.source]}</Badge>
        <Badge variant="outline">{tool.category}</Badge>
        {tool.providerKey && <Badge variant="outline">{tool.providerKey}</Badge>}
        {tool.endpointMethod && (
          <Badge variant="outline">
            {tool.endpointMethod} {tool.endpointPath}
          </Badge>
        )}
        {tool.isPopular && (
          <Badge className="bg-yellow-100 text-yellow-800">
            <Star className="mr-1 h-3 w-3" />
            Popular
          </Badge>
        )}
        {tool.isDestructive && (
          <Badge variant="destructive">
            <AlertTriangle className="mr-1 h-3 w-3" />
            Destructive
          </Badge>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input Form */}
        <Card>
          <CardHeader>
            <CardTitle>Input Parameters</CardTitle>
            <CardDescription>Configure the parameters for this tool</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {schema.properties && Object.keys(schema.properties).length > 0 ? (
              <>
                {Object.entries(schema.properties).map(([key, prop]) => (
                  <div key={key}>
                    {renderInputField(key, prop, schema.required?.includes(key) || false)}
                  </div>
                ))}
              </>
            ) : (
              <p className="text-muted-foreground text-sm">
                This tool has no input parameters
              </p>
            )}

            <Button
              onClick={handleInvoke}
              disabled={invoking}
              className="w-full"
            >
              {invoking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Execute Tool
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Result */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Result
              {result && (
                <Button variant="ghost" size="sm" onClick={handleCopyResult}>
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </CardTitle>
            {result && (
              <CardDescription className="flex items-center gap-2">
                {result.success ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500" />
                )}
                <span>{result.success ? 'Success' : 'Failed'}</span>
                <Clock className="h-4 w-4 ml-2" />
                <span>{result.durationMs}ms</span>
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {result ? (
              <pre className="bg-muted p-4 rounded-lg overflow-auto max-h-96 text-sm">
                {result.error
                  ? result.error
                  : typeof result.data === 'string'
                  ? result.data
                  : JSON.stringify(result.data, null, 2)}
              </pre>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Play className="mx-auto h-12 w-12 mb-4 opacity-50" />
                <p>Execute the tool to see results</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Schema Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Input Schema (JSON)</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="bg-muted p-4 rounded-lg overflow-auto text-sm max-h-64">
            {JSON.stringify(tool.inputSchema, null, 2)}
          </pre>
        </CardContent>
      </Card>

      {/* Tags */}
      {tool.tags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {tool.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
